/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable no-unused-expressions */

import * as yup from 'yup';
import { cloneDeep } from 'lodash';

function handleInitialAdjustments({ questionnaire }) {
  return questionnaire.map(question => {
    return question;
  })
};

function groupMultipleFieldQuestions({
  mainIdQuestion,
  questionnaire,
  handleQuestionsInitialAdjustments
}) {
  const groupQuestions = [];

  const mainQuestions = questionnaire.filter(question => question.idQuestion === mainIdQuestion);
  for (const mainQuestion of mainQuestions) {
    let group = []

    const mainQuestionIndex = questionnaire.findIndex(question => question.idQuestion === mainIdQuestion);
    if (mainQuestionIndex || mainQuestionIndex == 0) group.push(questionnaire[mainQuestionIndex]);

    if (mainQuestionIndex != -1) questionnaire.splice(mainQuestionIndex, 1);

    if (mainQuestion.childrenQuestions) mainQuestion.childrenQuestions.map(childQuestion => group.push(childQuestion));

    group.sort((a, b) => a.sequenceQuestion - b.sequenceQuestion);

    if (handleQuestionsInitialAdjustments) group = handleQuestionsInitialAdjustments({ questionnaire: group });

    groupQuestions.push(group.map((question, index) => ({ ...question, key: null })));
  }

  return groupQuestions;
}

function createSectionRow({
  questions
}) {
  questions = JSON.parse(JSON.stringify(questions));
  return questions.map(question => {
    const { defaultValue } = question.behaviorObject || {};
    question.answer = defaultValue || "";
    question.answerJson = defaultValue || "";
    question.idAnswerOption = null;
    question.idPerson = null;
    question.idQuestionnaireAnswers = null;
    question.idQuestionnaireAnswersParent = null;
    question.idQuestionnaireAnswersRoot = null;
    question.key = null;
    question.disabled = false;

    if (question.behaviorObject?.extraExclamationWarning) {
      question.behaviorObject.extraExclamationWarning = false;
    }

    return question;
  })
}

function handleSave({
  values,
  questionnaire,
  idOperation,
  idOperationParent,
  idProduct,
  SECTIONS,
  isDraft,
  mc,
  formikRef,
  refQuestionnaireSent,
  useGetQuestionnaireAnswersRequest,
  useSaveQuestionnaireAnswerRequest,
  questionnaireAnswersToDelete,
  useDeleteQuestionnaireAnswerRequest,
  documentsToDelete,
  useDeleteAllDocumentsByIdsRequest,
  submitFunction,
  fitinsurAPI,
  authContext,
  getInsurers,
}) {

  buildValidation({
    values,
    questionnaire,
    SECTIONS,
    isDraft,
  }).validate(values, { abortEarly: false }).then(async () => {
    const isValidSubmittedForm = await handleValidationsSubmittedForm({
      questionnaire,
      idOperation,
      idOperationParent,
      idProduct,
      SECTIONS,
      isDraft,
      mc,
      fitinsurAPI,
      authContext,
      getInsurers,
    });

    if (isValidSubmittedForm) {
      handleSubmit({
        values,
        questionnaire,
        idOperation,
        idOperationParent,
        onlyQuestions: false,
        SECTIONS,
        isDraft,
        refQuestionnaireSent,
        useGetQuestionnaireAnswersRequest,
        useSaveQuestionnaireAnswerRequest,
        questionnaireAnswersToDelete,
        useDeleteQuestionnaireAnswerRequest,
        documentsToDelete,
        useDeleteAllDocumentsByIdsRequest,
        submitFunction
      });
    }

  }).catch((error) => {
    onValidationError({
      error,
      mc,
      formikRef,
      questionnaire,
      groupQuestionnaires: [],
    })
  })
}

function buildValidation({
  values,
  questionnaire,
  SECTIONS,
  isDraft,
}) {
  const validationSchema = {};

  const validationQuestionnaire = handleQuestionnaireAnswerToSave({
    values,
    questionnaire,
    onlyQuestions: true,
    SECTIONS,
  });

  let allQuestions = [...validationQuestionnaire];
  for (const question of allQuestions) {
    if (question.childrenQuestions?.length) allQuestions = [...allQuestions, ...question.childrenQuestions]
  }

  for (const question of allQuestions) {
    if (isDraft && ![1378, 1379, 1380, 1381, 1382].includes(question.idQuestion)) continue;
    if ([614].includes(question?.idDomainQuestion)) continue;
    if (!question?.key) continue;

    question?.childrenQuestions?.map(item => {
      if (item.visibleQuestion && item.fieldMask?.type == 'phone' && item.behaviorObject?.isRequired) {
        validationSchema[item.key] = yup.string().test({
          name: "answer-validation",
          message: "Invalid phone",
          test: (value) => !!value && value.length >= 10
        })
      } else if (item.visibleQuestion && item.behaviorObject?.fieldMask == 'email') {
        if (item.answer) {
          validationSchema[item.key] = yup.string().email('Invalid email').required("Required field")
        } else {
          if (item.behaviorObject?.isRequired) {
            validationSchema[item.key] = yup.string().email("Invalid email").required("Required field")
          } else {
            validationSchema[item.key] = yup.string().notRequired();
          }
        }
      } else {
        validationSchema[item.key] = item.visibleQuestion && item.behaviorObject?.isRequired
          ? yup.string().test("answer-validation", "Required field", function (value) {
            return !!item.answer;
          }) : yup.string().notRequired();
      }
    })

    if (question.visibleQuestion && question.behaviorObject?.isRequired && question.fieldMask?.type == 'phone') {
      validationSchema[question.key] = yup.string().test({
        name: "answer-validation",
        message: "Invalid phone",
        test: (value) => !!value && value.length >= 10
      })
    } else if (question.visibleQuestion && question.behaviorObject?.fieldMask == 'email') {
      if (question.answer) {
        validationSchema[question.key] = yup.string().email("Invalid email").required("Required field")
      } else {
        if (question.behaviorObject?.isRequired) {
          validationSchema[question.key] = yup.string().email("Invalid email").required("Required field")
        } else {
          validationSchema[question.key] = yup.string().notRequired();
        }
      }
    } else {
      validationSchema[question.key] = question.visibleQuestion && question.behaviorObject?.isRequired
        ? yup.string().test("answer-validation", "Required field", function (value) {
          return !!question.answer;
        }) : yup.string().notRequired();
    }
  }

  return yup.object().shape(validationSchema);
}

async function handleValidationsSubmittedForm({
  questionnaire,
  idOperation,
  idOperationParent,
  idProduct,
  SECTIONS,
  isDraft,
  mc,
  fitinsurAPI,
  authContext,
  getInsurers,
}) {
  if (isDraft) return true;

  let validationResult = true;

  const policyWordingQst = questionnaire.find((qst) => qst.idQuestion === 1941);

  if (policyWordingQst.idQuestion === 1941) {
    const policyWordingAnswer = policyWordingQst.answer;
    if (!policyWordingAnswer?.length) {
      mc.error("Policy Wording Attachment is required!");
      validationResult = false;
    }
    
    const insurers = getInsurers();
    
    const hasOneWordingForAllInsurers = policyWordingAnswer.some((answer) => {
      return answer?.documentType?.idAnswerOption == 7686 && !answer?.whichInsurer.idWhichInsurer;
    });

    const hasOneWordingForEachInsurer = insurers.every((insurer) => {
      return policyWordingAnswer.some((answer) => {
        return answer?.documentType?.idAnswerOption == 7686 && answer?.whichInsurer?.idWhichInsurer == insurer.idPersonInsurer;
      })
    });

    if (!hasOneWordingForAllInsurers && !hasOneWordingForEachInsurer) {
      mc.error("You must attach a Policy Wording for each insurer or one for all insurers.");
      validationResult = false;
    }
  }

  const useMrc1 = questionnaire.find((qst) => qst.idQuestion === 1942);
  const useConnectionMrc1 = questionnaire.find((qst) => qst.idQuestion === 1943);

  if (useMrc1.idAnswerOption === 7592 && useConnectionMrc1.idQuestion === 1943 && useConnectionMrc1.idAnswerOption === 7593) {
    const mrc1AttachmentsQst = questionnaire.find((qst) => qst.idQuestion === 1961);

    if (!mrc1AttachmentsQst.answer.length) {
      mc.error("MRC1 attachment is required!");
      validationResult = false;
    }
  }

  const useMrc2 = questionnaire.find((qst) => qst.idQuestion === 1944);
  const useConnectionMrc2 = questionnaire.find((qst) => qst.idQuestion === 1945);

  if (useMrc2.idAnswerOption === 7596 && useConnectionMrc2.idQuestion === 1945 && useConnectionMrc2.idAnswerOption === 7597) {
    const mrc2AttachmentQsts = questionnaire.filter((qst) => qst.idQuestion === 1962)

    mrc2AttachmentQsts.forEach((qst) => {
      if (!qst.answer.length) {
        mc.error(`MRC2 attachment is required for insurer: ${qst.behaviorObject?.labelDescription}!`);
        validationResult = false;
      }
    })
  }

  const useConnectionPremiumSchedule = questionnaire.find((qst) => qst.idQuestion === 1947);

  if (useConnectionPremiumSchedule.idAnswerOption === 7602) {
    const premiumCalculationQst = questionnaire.find((qst) => qst.idQuestion === 1977)

    if (!premiumCalculationQst.answer.length) {
      mc.error("For Connexion to produce the Premium Schedules you must load a Premium Calculations file.");
      validationResult = false;
    }
  } else {
    const premiumScheduleAttachments = questionnaire.filter((qst) => qst.idQuestion === 1979);

    premiumScheduleAttachments.forEach((qst) => {
      if (!qst.answer.length) {
        mc.error(`Premium schedule attachment is required for insurer: ${qst.behaviorObject?.labelDescription}!`);
        validationResult = false;
      }
    })
  }

  const useConnectionDeclaration = questionnaire.find((qst) => qst.idQuestion === 1946);

  if (useConnectionDeclaration.idAnswerOption === 7599) {
    const declarationAttachment = questionnaire.find((qst) => qst.idQuestion === 1954);

    if (!declarationAttachment.answer.length) {
      mc.error("Declaration attachment is required!");
      validationResult = false;
    }
  } else {
    const isEnabledToCreatePolicyPack = await checkEnableToCreatePolicyPack({
      idOperation,
      idProduct,
      mc,
      fitinsurAPI,
      authContext,
    });

    if (!isEnabledToCreatePolicyPack) {
      mc.error("The declaration document needs to be edited to proceed!");
      validationResult = false
    }
  }

  return validationResult;
}

function onValidationError({
  error,
  mc,
  formikRef,
  questionnaire,
  groupQuestionnaires
}) {
  console.log('onValidationError catch initial', error)

  questionnaire = cloneDeep(questionnaire);
  groupQuestionnaires = cloneDeep(groupQuestionnaires);

  mc.error('Please complete all required fields to proceed.')
  const allQuestions = questionnaire;

  groupQuestionnaires.forEach((grpQuestionnaire) => {
    Array.isArray(grpQuestionnaire)
      ? grpQuestionnaire.map(question => allQuestions.push(question))
      : grpQuestionnaire.questions.map(question => allQuestions.push(question))
  });

  let firstErrorKey = null;
  try {
    for (const question of allQuestions) {
      if (!formikRef.current.errors || !formikRef.current.errors[question.key]) continue;
      formikRef.current.setFieldError(question.key, null);
    }

    error.inner.map(({ message, path }) => {
      const question = allQuestions.find(question => question.key === path);

      if (question.dsQuestion === "E-mail") {
        mc.error(`The email entered is invalid`);
        formikRef.current.setFieldError(question.key, `* Invalid email`);
      } else {
        mc.error(`The Field: ${question.dsQuestion} is required`);
        formikRef.current.setFieldError(question.key, `* Required field`);
      }

      if (!firstErrorKey) firstErrorKey = question.key;
    });

    if (firstErrorKey) {
      const elements = global.window.document.getElementsByName(firstErrorKey);
      if (elements?.length) {
        setTimeout(() => elements[0].scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' }), 300);
      }
    }
  } catch (e) {
    console.log('onValidationError catch', e)
  }
}

function handleQuestionnaireAnswerToSave({
  values,
  questionnaire,
  onlyQuestions,
  SECTIONS,
}) {
  let questionnaireAnswers = cloneDeep(questionnaire);

  for (const [, section] of Object.entries(SECTIONS)) {
    if (section.isMultiple) {
      let multipleFields = []
      switch (section.name) {
        default:
          multipleFields = [];
          break;
      }

      multipleFields.map(group => {
        if (group?.length) {
          const rootQuestion = group.find(question => !question.idQuestionRoot);
          const childrenQuestions = group.filter(question => question.idQuestionRoot);
          rootQuestion.childrenQuestions = childrenQuestions;
          if (!onlyQuestions) {
            questionnaireAnswers = [...questionnaireAnswers, rootQuestion];
          } else {
            questionnaireAnswers = [...questionnaireAnswers, ...group];
          }
        }
      })
    }
  }

  let childrenQuestionIds = [];
  for (const question of questionnaireAnswers) {
    if (question) {
      if (
        (
          question.idDomainQuestion === 270 ||
          question.idDomainQuestion === 271 ||
          (question.idDomainQuestion === 44 && typeof question.answer === 'object')
        ) && question.answer
      ) {
        if (!(question.idQuestion === 1977 && typeof question.answer === 'string')) {
          try {
            question.answer = JSON.stringify(question.answer)
          } catch (e) {
          }
        }
      }

      if (SECTIONS[question.behaviorObject?.sectionName] && SECTIONS[question.behaviorObject?.sectionName].isMultiple) {
        if (question?.childrenQuestions?.length) {
          const idQuestions = question.childrenQuestions.map(question => question.idQuestion);
          childrenQuestionIds = [...childrenQuestionIds, ...idQuestions];
        }
      } else {
        question.childrenQuestions = [];
      }
    }
  }

  questionnaireAnswers = questionnaireAnswers.filter(v => !childrenQuestionIds.includes(v.idQuestion))

  return questionnaireAnswers;
}

function handleSubmit({
  values,
  questionnaire,
  idOperation,
  idOperationParent,
  onlyQuestions,
  SECTIONS,
  isDraft,
  refQuestionnaireSent,
  useGetQuestionnaireAnswersRequest,
  useSaveQuestionnaireAnswerRequest,
  questionnaireAnswersToDelete,
  useDeleteQuestionnaireAnswerRequest,
  documentsToDelete,
  useDeleteAllDocumentsByIdsRequest,
  submitFunction
}) {

  if (refQuestionnaireSent.current) return;

  refQuestionnaireSent.current = true;

  const validQuestionnaire = handleQuestionnaireAnswerToSave({
    values,
    questionnaire,
    onlyQuestions,
    SECTIONS,
  });

  useSaveQuestionnaireAnswerRequest(validQuestionnaire)
    .then((resp) => {
      if (resp.status === 200) {
        if (idOperation && questionnaireAnswersToDelete?.length) {
          useDeleteQuestionnaireAnswerRequest({ ids: questionnaireAnswersToDelete });
        }

        if (idOperation && documentsToDelete?.length) {
          useDeleteAllDocumentsByIdsRequest({ ids: documentsToDelete });
        }

        useGetQuestionnaireAnswersRequest();

        if (!isDraft && submitFunction) {
          submitFunction();
        } else {
          window.location.reload();
        }
      }
    })
    .catch((e) => {
      console.log('handleSubmit - error', e)
    })
    .finally(() => {
      refQuestionnaireSent.current = false;
    });
}

async function checkEnableToCreatePolicyPack({
  idOperation,
  idProduct,
  mc,
  fitinsurAPI,
  authContext,
}) {
  if (idProduct !== 16) return true;
  let success = false;

  fitinsurAPI.setAuthToken(authContext.token);
  await fitinsurAPI.requestGetEnablePolicy(idOperation)
    .then(data => data.json())
    .then(resp => {
      if (resp.ret_code !== 200) {
        mc.erro(resp.ret_msg_detail);
        return;
      };

      if ("isEnabledBtnPolicyPack" in resp.ret_value) {
        if (resp.ret_value.isEnabledBtnPolicyPack) {
          success = true;
        }
      };
    })
    .catch((error) => {
      mc.error(`There was an error while fetching your data: ${checkEnableToCreatePolicyPack.name}\n` + error);
      console.error('checkEnableToCreatePolicyPack - Error:', error);
    });

  return success;
};

const events = {
  handleInitialAdjustments,
  groupMultipleFieldQuestions,
  createSectionRow,
  handleSave,
};

export { events };